// Bones 🦴 — the spendable currency earned by logging transactions. This is NOT the
// read-only "Treats" honey jar (a budget-adherence metric with no balance). Bones are
// persisted in localStorage with an idempotent, per-event ledger so logging can't be
// farmed: every credit is keyed by a STABLE id (the transaction's row id, or a
// once-per-day key), deletes are never refunded, and a daily cap blocks spam.

const WALLET_KEY = "pawket.bones.wallet" // { balance, lifetime }
const LEDGER_KEY = "pawket.bones.ledger" // string[] of awarded keys
const DAYCAP_KEY = "pawket.bones.daycap" // { date, earnedToday }
export const BONES_EVENT = "pawket:bones-changed"

const DAILY_CAP = 60
const LEDGER_MAX = 600

export const BONE_REWARDS = {
  log: 5,
  firstDaily: 10,
  noSpend: 10,
  milestone: { 3: 25, 7: 50, 14: 100, 30: 250, 100: 1000 } as Record<number, number>,
}

interface Wallet { balance: number; lifetime: number }

function emit(): void {
  try { window.dispatchEvent(new CustomEvent(BONES_EVENT)) } catch { /* no window */ }
}
function readJSON<T>(key: string, fallback: T): T {
  try { const r = localStorage.getItem(key); return r ? (JSON.parse(r) as T) : fallback } catch { return fallback }
}
function writeJSON(key: string, v: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(v)) } catch { /* quota / no window */ }
}
function todayKey(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}
function getWallet(): Wallet {
  const w = readJSON<Wallet>(WALLET_KEY, { balance: 0, lifetime: 0 })
  return { balance: Number(w.balance) || 0, lifetime: Number(w.lifetime) || 0 }
}
function getLedger(): string[] {
  const l = readJSON<string[]>(LEDGER_KEY, [])
  return Array.isArray(l) ? l : []
}
function getDayCap(): { date: string; earnedToday: number } {
  const c = readJSON(DAYCAP_KEY, { date: todayKey(), earnedToday: 0 })
  return c.date === todayKey() ? c : { date: todayKey(), earnedToday: 0 }
}

export function getBonesBalance(): number { return getWallet().balance }
export function getBonesLifetime(): number { return getWallet().lifetime }
export function todayDateKey(): string { return todayKey() }

/**
 * Idempotent credit. `key` must be stable (e.g. `txn:<rowId>`, `firstDaily:<date>`,
 * `milestone:<n>`). Returns Bones actually granted (0 if duplicate or daily cap hit).
 * The key is recorded even when the cap zeroes the grant, so a replay can't re-award.
 */
export function awardBones(key: string, amount: number): number {
  if (!key || !Number.isFinite(amount) || amount <= 0) return 0
  const ledger = getLedger()
  if (ledger.includes(key)) return 0
  const cap = getDayCap()
  const granted = Math.max(0, Math.min(amount, DAILY_CAP - cap.earnedToday))
  ledger.push(key)
  if (ledger.length > LEDGER_MAX) ledger.splice(0, ledger.length - LEDGER_MAX)
  writeJSON(LEDGER_KEY, ledger)
  if (granted > 0) {
    const w = getWallet()
    writeJSON(WALLET_KEY, { balance: w.balance + granted, lifetime: w.lifetime + granted })
    writeJSON(DAYCAP_KEY, { date: todayKey(), earnedToday: cap.earnedToday + granted })
    emit()
  }
  return granted
}

/** Credit a freshly-logged transaction: a flat per-log reward + a first-of-the-day bonus. */
export function awardForLoggedTransaction(txnId: string): number {
  if (!txnId) return 0
  let total = 0
  total += awardBones(`txn:${txnId}`, BONE_REWARDS.log)
  total += awardBones(`firstDaily:${todayKey()}`, BONE_REWARDS.firstDaily)
  return total
}

/**
 * One-time milestone grant — idempotent (recorded in the same ledger as logging
 * rewards, keyed e.g. `collection:slot:hat`) but NOT subject to the daily logging
 * cap. Used for collection-completion bonuses: the user already SPENT Bones to own
 * every item in a set, so there's no farm vector, and the reward must actually land
 * even late in the day. Returns Bones granted (0 if already claimed). */
export function awardMilestoneBones(key: string, amount: number): number {
  if (!key || !Number.isFinite(amount) || amount <= 0) return 0
  const ledger = getLedger()
  if (ledger.includes(key)) return 0
  ledger.push(key)
  if (ledger.length > LEDGER_MAX) ledger.splice(0, ledger.length - LEDGER_MAX)
  writeJSON(LEDGER_KEY, ledger)
  const w = getWallet()
  writeJSON(WALLET_KEY, { balance: w.balance + amount, lifetime: w.lifetime + amount })
  emit()
  return amount
}

/** Spend Bones (buying an outfit). Returns true on success, false if short. */
export function spendBones(amount: number): boolean {
  if (!Number.isFinite(amount) || amount <= 0) return amount === 0
  const w = getWallet()
  if (w.balance < amount) return false
  writeJSON(WALLET_KEY, { balance: w.balance - amount, lifetime: w.lifetime })
  emit()
  return true
}

// ── Cross-device sync surface ─────────────────────────────────────────────
// Read the full wallet + the idempotent award ledger, and overwrite both from a
// synced remote snapshot. Used by wardrobe-sync.ts; the wallet stays the local
// source of truth, this just reconciles it with Supabase.

export function getBonesWallet(): Wallet { return getWallet() }
export function getBonesLedger(): string[] { return getLedger() }

/** Overwrite wallet + ledger from a reconciled remote snapshot, then notify. */
export function applyRemoteBones(input: { balance: number; lifetime: number; ledger: string[] }): void {
  const balance = Math.max(0, Math.round(Number(input.balance) || 0))
  const lifetime = Math.max(0, Math.round(Number(input.lifetime) || 0))
  writeJSON(WALLET_KEY, { balance, lifetime })
  const ledger = Array.isArray(input.ledger) ? input.ledger.filter((k) => typeof k === "string") : []
  writeJSON(LEDGER_KEY, ledger.slice(-LEDGER_MAX))
  emit()
}
